import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  getAdvanceIndex,
  initialPlaybackQueue,
  playbackQueueReducer,
  persistPlaybackQueue,
  restorePlaybackQueue,
} from '../lib/playbackQueue';
import {
  getAutoplayRecommendations,
  recordAutoplayEvent,
} from '../services/recommendationService';

const QueueContext = createContext(null);

export const QueueProvider = ({ children }) => {
  const [state, dispatch] = useReducer(playbackQueueReducer, initialPlaybackQueue, () => restorePlaybackQueue());
  const { items: queue, currentIndex } = state;

  useEffect(() => {
    persistPlaybackQueue(state);
  }, [state]);

  const addToQueue = useCallback((track) => dispatch({ type: 'ADD', payload: { track } }), []);
  const addMultipleToQueue = useCallback((tracks) => dispatch({ type: 'ADD_MANY', payload: { tracks } }), []);
  const removeFromQueue = useCallback((trackId) => dispatch({ type: 'REMOVE', payload: { trackId } }), []);
  const playNext = useCallback((track) => dispatch({ type: 'PLAY_NEXT', payload: { track } }), []);
  const clearQueue = useCallback(() => dispatch({ type: 'CLEAR' }), []);
  const reorderQueue = useCallback((fromIndex, toIndex) => dispatch({ type: 'REORDER', payload: { fromIndex, toIndex } }), []);
  const setPlayingIndex = useCallback((index) => dispatch({ type: 'SET_CURRENT', payload: { index } }), []);
  const toggleShuffle = useCallback(() => dispatch({ type: 'TOGGLE_SHUFFLE' }), []);
  const cycleRepeat = useCallback(() => dispatch({ type: 'CYCLE_REPEAT' }), []);
  const setAutoplay = useCallback((enabled) => dispatch({ type: 'SET_AUTOPLAY', payload: { enabled } }), []);
  const replaceQueue = useCallback((tracks, options = {}) => dispatch({
    type: 'REPLACE',
    payload: { items: tracks, currentIndex: options.currentIndex, source: options.source },
  }), []);

  const getNextTrack = useCallback(() => {
    const nextIndex = getAdvanceIndex(state);
    return nextIndex >= 0 ? queue[nextIndex] || null : null;
  }, [queue, state]);

  const getPrevTrack = useCallback(() => (
    currentIndex > 0 ? queue[currentIndex - 1] : null
  ), [currentIndex, queue]);

  const advanceQueue = useCallback(() => {
    const nextIndex = getAdvanceIndex(state);
    if (nextIndex < 0) return null;
    const nextTrack = queue[nextIndex];
    dispatch({ type: 'ADVANCE', payload: { index: nextIndex } });
    return nextTrack;
  }, [queue, state]);

  const advanceOrAutoplay = useCallback(async () => {
    const nextIndex = getAdvanceIndex(state);
    if (nextIndex >= 0) {
      const nextTrack = queue[nextIndex];
      dispatch({ type: 'ADVANCE', payload: { index: nextIndex } });
      return nextTrack;
    }

    if (!state.autoplay) return null;
    const seed = currentIndex >= 0 ? queue[currentIndex] : queue[queue.length - 1];
    if (!seed) return null;

    recordAutoplayEvent('autoplay_requested', {
      seed_id: seed.track_id,
      queue_length: queue.length,
    });

    try {
      const recommendations = await getAutoplayRecommendations({
        seed,
        queue,
        history: state.history,
        limit: 5,
      });
      if (!recommendations.length) {
        recordAutoplayEvent('autoplay_empty', { seed_id: seed.track_id });
        return null;
      }

      const additions = recommendations.map((track) => ({
        ...track,
        autoplay_added: true,
        recommendation_reason: track.reason,
        recommendation_source: track.source,
        recommendation_score: track.score,
      }));
      const firstIndex = queue.length;
      dispatch({ type: 'ADD_MANY', payload: { tracks: additions } });
      dispatch({ type: 'ADVANCE', payload: { index: firstIndex } });
      recordAutoplayEvent('autoplay_added', {
        seed_id: seed.track_id,
        count: additions.length,
        recommendation_source: additions[0].recommendation_source,
        first_track_id: additions[0].track_id,
      });
      return additions[0];
    } catch (error) {
      recordAutoplayEvent('autoplay_failed', {
        seed_id: seed.track_id,
        message: error?.message || 'Unknown recommendation error',
      });
      return null;
    }
  }, [currentIndex, queue, state]);

  const value = useMemo(() => ({
    queue,
    currentIndex,
    currentTrack: currentIndex >= 0 ? queue[currentIndex] || null : null,
    history: state.history,
    queueSource: state.source,
    shuffle: state.shuffle,
    repeatMode: state.repeatMode,
    autoplay: state.autoplay,
    updatedAt: state.updatedAt,
    addToQueue,
    addMultipleToQueue,
    removeFromQueue,
    playNext,
    clearQueue,
    reorderQueue,
    replaceQueue,
    getNextTrack,
    getPrevTrack,
    setPlayingIndex,
    advanceQueue,
    advanceOrAutoplay,
    toggleShuffle,
    cycleRepeat,
    setAutoplay,
    queueLength: queue.length,
  }), [
    addMultipleToQueue,
    addToQueue,
    advanceOrAutoplay,
    advanceQueue,
    clearQueue,
    currentIndex,
    cycleRepeat,
    getNextTrack,
    getPrevTrack,
    playNext,
    queue,
    removeFromQueue,
    reorderQueue,
    replaceQueue,
    setAutoplay,
    setPlayingIndex,
    state.autoplay,
    state.history,
    state.repeatMode,
    state.shuffle,
    state.source,
    state.updatedAt,
    toggleShuffle,
  ]);

  return <QueueContext.Provider value={value}>{children}</QueueContext.Provider>;
};

export const useQueue = () => {
  const context = useContext(QueueContext);
  if (!context) throw new Error('useQueue must be used within QueueProvider');
  return context;
};

export default QueueContext;
